import * as React from 'react'
// libs
import { matchPath } from 'react-router'
import { useRouter } from 'hooks/useRouter'
import { useSelector } from 'react-redux'
import { useMutation, useQuery } from '@apollo/client/react'
// context
import { LinkingModeContext } from 'containers/Plan/Edit/layers/API/Linking'
import { PlanEditPageContext } from 'containers/Plan/Edit/layers/API'
// constants
import { OBJECT_ENTITY_NAMES } from 'constants/objects'
import { ENTITIES, INPUT_METHOD_VALUES, KPI_RESULT_TYPE_VALUES, PLAN_VIEW_MODE } from 'constants/index'
// graphql
import {
  LINKING_ITEM_CREATE_MUTATION,
  LINKING_ITEM_DELETE_MUTATION,
  PLAN_ITEM_CREATE_MUTATION,
  PLAN_ITEM_DELETE_MUTATION,
  PLAN_TREE_UPDATE_MUTATION
} from 'graphql/plan/mutations'
import { PLAN_DETAILS_TREE_QUERY } from 'graphql/plan/queries'
// store
import { objectEntitiesSelector } from 'store/reducers/objectEntities'
// types
import {
  IPlan,
  IPlanItem,
  IFieldOption,
  IPlanItemResponse,
  ILinkingItemResponse,
  ILinkingItemCreateInput
} from 'types/graphql'
// lodash
import get from 'lodash/get'
import pipe from 'lodash/fp/pipe'
import find from 'lodash/fp/find'
import getFp from 'lodash/fp/get'
// utils
import { getFieldId } from 'utils/form'
import { handleError } from 'utils/errors/handlers'
import { getFieldsList } from 'utils/fields'
import { getPlanColumnVariables } from 'utils/planColumns'
import { buildPositionsWithArchived } from './utils'

type Props = React.PropsWithChildren<{ plan?: IPlan }>

type PlanItemCreateResponse = {
  planItemCreate: IPlanItemResponse
}

type LinkingItemCreateResponse = {
  linkingItemCreate: ILinkingItemResponse
}

interface IContext {
  createPlanItem: ({
    entity,
    name,
    planId,
    parentId,
    lastPosition,
    typeOptionId
  }: {
    entity: string
    name: string
    planId: string
    parentId: string
    lastPosition: number
    typeOptionId?: string
  }) => Promise<IPlanItem>
  createRootEntityFormStateRef: any
  deletePlanItem: (id: string) => void
  isLoading: boolean
  linkPlanItem: ({ linkingItemCreateInput }: { linkingItemCreateInput: ILinkingItemCreateInput }) => Promise<void>
  planItems: IPlanItem[]
  unlinkPlanItem: (id: string, refetchQueries: any) => Promise<void>
  updatePlanTree: (planItems: any) => void
}

export const ObjectivesListAPIContext = React.createContext<IContext & any>({} as any)

const ObjectivesListAPI: React.FC<Props> = ({ children, plan }) => {
  const router = useRouter()

  const { data: planTreeQuery, loading } = useQuery(PLAN_DETAILS_TREE_QUERY, {
    skip: !!matchPath('/plans/:companyId/edit/:id', router.pathname),
    fetchPolicy: 'network-only',
    variables: {
      ...getPlanColumnVariables(plan?.columnSettings),
      filter: {
        planId: router.query.id,
        referenced: router.query.planViewMode === PLAN_VIEW_MODE.PLAN ? false : null
      }
    }
  })

  const [treeUpdateMutation] = useMutation(PLAN_TREE_UPDATE_MUTATION)
  const [deletePlanItemMutation] = useMutation(PLAN_ITEM_DELETE_MUTATION)
  const [createPlanItemMutation] = useMutation<PlanItemCreateResponse>(PLAN_ITEM_CREATE_MUTATION)
  const [createLinkingItemMutation] = useMutation<LinkingItemCreateResponse>(LINKING_ITEM_CREATE_MUTATION)
  const [deleteLinkingItemMutation] = useMutation(LINKING_ITEM_DELETE_MUTATION)

  const objectEntities = useSelector(objectEntitiesSelector)
  const objectEntitiesRef = React.useRef(objectEntities)

  React.useEffect(() => {
    objectEntitiesRef.current = objectEntities
  }, [objectEntities])

  const { treeQuery, treeQueryLoading, treeQueryRefetch, showArchivedPlanItems } = React.useContext(PlanEditPageContext)
  const { isLinkingMode, refetchQuery } = React.useContext(LinkingModeContext)

  const createRootEntityFormStateRef = React.useRef(false)
  const createRootEntityRootTypeRef = React.useRef(false)

  const treeData = treeQuery || planTreeQuery
  const isLoading = loading || treeQueryLoading

  const planItems = get(treeData, 'planItems.data')

  // methods
  const createPlanItem = async ({ entityType, name, planId, parentId, lastPosition, typeOptionId }) => {
    let entityInput
    const currentObjectEntities = objectEntitiesRef.current

    if (entityType === ENTITIES.PLAN_ACTION) {
      const fields = getFieldsList(currentObjectEntities, OBJECT_ENTITY_NAMES.ACTION)

      entityInput = {
        planActionInput: {
          description: name,
          fieldValues: [{ fieldId: getFieldId(fields, 'description'), value: name }]
        }
      }
    }
    if (entityType === ENTITIES.PLAN_KPI) {
      const fields = getFieldsList(currentObjectEntities, OBJECT_ENTITY_NAMES.KPI)

      const resultType = pipe(
        find(({ name }) => name === 'resultInputType'),
        getFp('options'),
        find((option: IFieldOption) => option.value === KPI_RESULT_TYPE_VALUES.VALUE)
      )(fields)

      const inputMethod = pipe(
        find(({ name }) => name === 'inputMethod'),
        getFp('options'),
        find(({ value }) => value === INPUT_METHOD_VALUES.USER_INPUT),
        (option: IFieldOption) => ({ value: option.id })
      )(fields)

      entityInput = {
        planKPIInput: {
          resultType: resultType.value,
          description: name,
          fieldValues: [
            { fieldId: getFieldId(fields, 'description'), value: name },
            { fieldId: getFieldId(fields, 'inputMethod'), value: inputMethod },
            { fieldId: getFieldId(fields, 'resultInputType'), value: { value: resultType.id } }
          ]
        }
      }
    }
    if (entityType === ENTITIES.PLAN_OBJECTIVE) {
      const fields = getFieldsList(currentObjectEntities, OBJECT_ENTITY_NAMES.OBJECTIVE)

      entityInput = {
        planObjectiveInput: {
          description: name,
          ...(typeOptionId && { typeOptionId }),
          fieldValues: [{ fieldId: getFieldId(fields, 'description'), value: name }]
        }
      }
    }

    try {
      const {
        data: {
          planItemCreate: { data: entity }
        }
      } = await createPlanItemMutation({
        variables: {
          input: {
            planId,
            parentId,
            treePosition: lastPosition,
            ...entityInput
          }
        }
      })

      treeQueryRefetch()
      return entity
    } catch (errors) {
      handleError(errors, 'ObjectivesListAPI.createPlanItem')
    }
  }

  const deletePlanItem = async (id: string) => {
    try {
      await deletePlanItemMutation({
        variables: {
          id: id,
          deleteSource: false
        }
      })

      await treeQueryRefetch()
    } catch (errors) {
      handleError(errors, 'ObjectivesListAPI.deletePlanItem')
    }
  }

  const linkPlanItem = async ({ linkingItemCreateInput }) => {
    try {
      const {
        data: {
          linkingItemCreate: { data }
        }
      } = await createLinkingItemMutation({
        variables: {
          input: linkingItemCreateInput
        },
        refetchQueries: isLinkingMode ? refetchQuery : []
      })

      await treeQueryRefetch()

      return data
    } catch (errors) {
      handleError(errors, 'ObjectivesListAPI.linkPlanItem')
    }
  }

  const unlinkPlanItem = async (id, refetchQuery) => {
    try {
      await deleteLinkingItemMutation({
        variables: {
          id: id
        },
        refetchQueries: refetchQuery
      })

      await treeQueryRefetch()
    } catch (errors) {
      handleError(errors, 'ObjectivesListAPI.unlinkPlanItem')
    }
  }

  const updatePlanTree = async (visibleItems: any[]) => {
    const allItems: IPlanItem[] = planItems ?? []
    const input = showArchivedPlanItems ? visibleItems : buildPositionsWithArchived(visibleItems, allItems)

    try {
      await treeUpdateMutation({ variables: { input } })
      treeQueryRefetch()
    } catch (error) {
      handleError(error, 'ObjectivesListAPI.updatePlanTree')
    }
  }

  const value = {
    createPlanItem,
    createRootEntityFormStateRef,
    createRootEntityRootTypeRef,
    deletePlanItem,
    isLoading,
    linkPlanItem,
    planItems,
    unlinkPlanItem,
    updatePlanTree
  }

  return <ObjectivesListAPIContext.Provider value={value}>{children}</ObjectivesListAPIContext.Provider>
}

export default ObjectivesListAPI
